import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { EMPTY, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

// One interactive recovery at a time: a page load fires several BE calls in parallel, and every
// one of them would otherwise start its own acquireTokenRedirect while the first is already
// tearing the page down.
let interactiveRecoveryStarted = false;

// Unsigned local JWT for be-hotel-extranet-service (local profile, oidc disabled).
// The B2B portal is an AGENCY surface, so it authenticates as AGENCY_MANAGER (head agency
// ah.abdellatif96@gmail.com) — this drives the agency-scoped reservation listing (#14).
// Only used when environment.skipAuth is true (see below).
const EXTRANET_DEV_JWT = 'Bearer eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0.' +
  'eyJlbWFpbHMiOlsiYWguYWJkZWxsYXRpZjk2QGdtYWlsLmNvbSJdLCJleHRlbnNpb25fcm9sZSI6IkFHRU5DWV9NQU5BR0VSIiwic3ViIjoiYWdlbmN5LW1hbmFnZXItbG9jYWwifQ.' +
  'fakesig';

/** Attaches an Authorization header to BE calls. Two modes, chosen by environment.skipAuth:
 *  - skipAuth=true (local convenience): a fake unsigned JWT, matching what the local BE
 *    profiles accept without verifying a signature.
 *  - skipAuth=false (Milestone 2 — dev-cloud/prod): the real Azure AD B2C access token for the
 *    signed-in MSAL account, scoped to environment.apiScopes. be-agency-service is the one BE
 *    that may enforce real oidc validation outside local — if it 401s here, that's a BE-contract
 *    question for the BE owners, not something to fix by editing be-agency-service (frozen). */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // be-utils-service is a public APIM endpoint — no auth header, in either mode.
  if (req.url.includes('/api/utils')) return next(req);

  if (!environment.skipAuth) {
    const msal = inject(MSAL_INSTANCE) as unknown as IPublicClientApplication;
    const account = msal.getActiveAccount();
    if (!account || !environment.apiScopes.length) return next(req);
    return from(msal.acquireTokenSilent({ scopes: environment.apiScopes, account })).pipe(
      // catchError sits BEFORE switchMap on purpose: it must only catch a token-acquisition
      // failure, not swallow HTTP errors from the actual request below and silently retry
      // without auth (that previously masked real BE errors, e.g. a 400 becoming an
      // unauthenticated retry that then 503'd).
      catchError((error) => {
        // The cached account's session is over (the B2C refresh token expired, the account
        // needs consent, ...): silent renewal can only keep failing, and proceeding without a
        // token is worse than failing — the BEs serve agency-scoped surfaces anonymously, so
        // the portal keeps "working" with every price flagged unavailable and the agency's
        // Extranet inventory gone, which reads as a data regression instead of an expired
        // session. Re-authenticate via redirect and complete without issuing the request:
        // the page is being torn down by the redirect anyway.
        if (error instanceof InteractionRequiredAuthError) {
          if (!interactiveRecoveryStarted) {
            interactiveRecoveryStarted = true;
            void msal.acquireTokenRedirect({ scopes: environment.apiScopes, account });
          }
          return EMPTY;
        }
        // Anything else (network blip, MSAL hiccup) keeps the old fallback: send the request
        // without a token rather than block the portal on a transient failure.
        return of(null);
      }),
      switchMap(result => next(result
        ? req.clone({ setHeaders: { Authorization: `Bearer ${result.accessToken}` } })
        : req)),
    );
  }

  // be-agency-service + be-b2b-user-management-service need a real AAD JWT.
  // Set environment.devAgencyToken to a token copied from flight-fusion B2B DEV login.
  if (req.url.includes('/api/agency') || req.url.includes('/api/b2bu')) {
    const token = environment.devAgencyToken;
    if (!token || token.includes('PASTE_TOKEN')) return next(req); // skip if placeholder
    return next(req.clone({ setHeaders: { Authorization: token } }));
  }
  // Catch-all for the remaining local backends — be-hotel-extranet-service, be-flight-search-
  // service, be-flight-reservation-service (all local, oidc disabled; flight-reservation only
  // needs the email claim to resolve the agency) — unsigned fake JWT is fine.
  return next(req.clone({ setHeaders: { Authorization: EXTRANET_DEV_JWT } }));
};
